/* Banking Portal · Waves 32/33/38 (Feb 2026).
   Three posture + compliance pages:
     Wave-32 · /banking/encryption-posture   — NYDFS § 500.15 alignment
     Wave-33 · /banking/auth-posture          — FFIEC auth posture
     Wave-38 · /banking/regulatory-calendar   — Board-level deliverables
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  KeyRound, Fingerprint, CalendarCheck2, ShieldCheck, AlertTriangle,
  CheckCircle2, XCircle, Lock, RefreshCw, Clock, FileCheck2, Users,
  Hash, Activity, ChevronRight, AlertOctagon, BookOpen,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function dateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function ts(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const TONE_CLS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  slate:   "bg-slate-50 text-slate-700 border-slate-200",
};

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const cls = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-32 · ENCRYPTION + KEY MANAGEMENT POSTURE
// ═════════════════════════════════════════════════════════════════════════
export function BankingEncryptionPosture() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/partner/banking/encryption-posture")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load encryption posture"));
  }, []);

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="banking-encryption-posture">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-violet-500" />
            Encryption + Key Management
          </h1>
          <Badge className="bg-violet-50 text-violet-700 border-violet-200">Wave 32</Badge>
          <Badge variant="outline" className="text-slate-500">NYDFS § 500.15</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="At-rest collections" value={`${data.summary.at_rest_emerald}/${data.summary.at_rest_collections}`} sub="Encrypted" tone="emerald" icon={Lock} testid="kpi-at-rest" />
        <Kpi label="In-transit surfaces" value={`${data.summary.in_transit_emerald}/${data.summary.in_transit_surfaces}`} sub="Modern TLS" tone="emerald" icon={ShieldCheck} testid="kpi-in-transit" />
        <Kpi label="Keys inventoried" value={`${data.summary.keys_present}/${data.summary.keys_inventoried}`} sub="Configured" tone="sky" icon={KeyRound} testid="kpi-keys" />
        <Kpi label="Disclosed gaps" value={fmt(data.summary.disclosed_gaps)} sub="Honestly tracked" tone="rose" icon={AlertTriangle} testid="kpi-enc-gaps" />
      </div>

      <Tabs defaultValue="at-rest" className="w-full">
        <TabsList>
          <TabsTrigger value="at-rest" data-testid="tab-at-rest">At-rest</TabsTrigger>
          <TabsTrigger value="in-transit" data-testid="tab-in-transit">In-transit (TLS)</TabsTrigger>
          <TabsTrigger value="keys" data-testid="tab-keys">Key inventory</TabsTrigger>
          <TabsTrigger value="gaps" data-testid="tab-enc-gaps">Honest gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="at-rest" className="pt-4 space-y-2">
          {data.at_rest.map(r => (
            <div key={r.collection} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`at-rest-${r.collection}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-mono text-[12.5px] text-slate-800 font-medium">{r.collection}</div>
                  <div className="text-[10.5px] text-slate-500">{r.scope}</div>
                </div>
                <Badge className={TONE_CLS[r.tone]}>{r.algorithm}</Badge>
              </div>
              <div className="mt-1 text-[11px] text-slate-600">{r.detail}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="in-transit" className="pt-4 space-y-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12px]" data-testid="tls-live-check">
            <div className="font-semibold text-slate-800 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" /> Live TLS handshake · {data.tls_live_check.hostname}
            </div>
            <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div>TLS version: <span className="font-mono">{data.tls_live_check.tls_version || "n/a"}</span></div>
              <div>Issuer: <span className="font-mono">{data.tls_live_check.issuer || "—"}</span></div>
              <div>Cert expires: <Badge className={TONE_CLS[data.tls_live_check.expiry_tone]}>{data.tls_live_check.days_until_expiry != null ? `${data.tls_live_check.days_until_expiry}d` : "—"}</Badge></div>
              <div>OK: {data.tls_live_check.ok ? "✓" : "✗"}</div>
            </div>
          </div>
          {data.in_transit.map((r, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`in-transit-${i}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-medium text-slate-800 text-[12.5px]">{r.surface}</div>
                <Badge className={TONE_CLS[r.tone]}>{r.tls_version}</Badge>
              </div>
              <div className="mt-1 text-[11px] text-slate-600">{r.detail}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="keys" className="pt-4 space-y-2">
          {data.key_management.map(k => (
            <div key={k.key} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`key-${k.key}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-mono text-[12.5px] text-slate-800 font-medium">{k.key}</div>
                  <div className="text-[10.5px] text-slate-500">{k.purpose}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={k.present ? TONE_CLS.emerald : TONE_CLS.rose}>{k.present ? "present" : "missing"}</Badge>
                  <Badge className={TONE_CLS[k.tone]}>rotate: {k.rotation_target}</Badge>
                </div>
              </div>
              <div className="mt-1 text-[11px] text-slate-600">
                Storage: <span className="font-mono">{k.storage}</span>
                {k.age_days != null && <> · last rotated {k.age_days}d ago</>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="gaps" className="pt-4 space-y-2">
          {data.gaps.map((g, i) => (
            <div key={i} className="rounded-xl border border-rose-200 bg-rose-50/40 p-3" data-testid={`enc-gap-${i}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-semibold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-500" />{g.gap}
                </div>
                <Badge className={TONE_CLS[g.severity === "high" ? "rose" : g.severity === "medium" ? "amber" : "slate"]}>{g.severity}</Badge>
              </div>
              <div className="mt-1 text-[11.5px] text-slate-700">{g.detail}</div>
              <div className="mt-1 text-[11px] text-slate-500 italic">Remediation: {g.remediation}</div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700" data-testid="enc-disclaimer">
        <div className="font-semibold text-slate-800 flex items-center gap-2 mb-1.5">
          <BookOpen className="w-4 h-4" /> Honest posture disclaimer
        </div>
        {data.disclaimer}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Wave-33 · AUTHENTICATION POSTURE
// ═════════════════════════════════════════════════════════════════════════
export function BankingAuthPosture() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/partner/banking/auth-posture")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load auth posture"));
  }, []);

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="banking-auth-posture">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Fingerprint className="w-6 h-6 text-sky-500" />
            Authentication Posture
          </h1>
          <Badge className="bg-sky-50 text-sky-700 border-sky-200">Wave 33</Badge>
          <Badge variant="outline" className="text-slate-500">FFIEC Auth Guidance</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Admin MFA" value={`${data.summary.admin_mfa_pct}%`} sub="Any-form coverage" tone={data.summary.admin_mfa_pct >= 95 ? "emerald" : "amber"} icon={Users} testid="kpi-admin-mfa" />
        <Kpi label="Password FFIEC" value={data.summary.password_ffiec_ok ? "✓ OK" : "✗ gap"} sub={`bcrypt · min-len ${data.password_policy.min_length}`} tone={data.summary.password_ffiec_ok ? "emerald" : "amber"} icon={Lock} testid="kpi-password-ok" />
        <Kpi label="Lockout FFIEC" value={data.summary.lockout_ffiec_ok ? "✓ OK" : "✗ gap"} sub={`${data.lockout_attestation.threshold_attempts}/${data.lockout_attestation.window_minutes}min`} tone={data.summary.lockout_ffiec_ok ? "emerald" : "amber"} icon={AlertOctagon} testid="kpi-lockout-ok" />
        <Kpi label="Impossible-travel" value={data.summary.impossible_travel_live ? "Live" : "Off"} sub={`${data.impossible_travel.alerts_30d} alerts · 30d`} tone="emerald" icon={Activity} testid="kpi-impossible-travel" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-sky-500" />MFA coverage by role</div>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[11.5px]">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right">TOTP</th>
                <th className="px-3 py-2 text-right">SMS</th>
                <th className="px-3 py-2 text-right">Dual MFA</th>
                <th className="px-3 py-2 text-right">Any MFA %</th>
              </tr>
            </thead>
            <tbody>
              {data.mfa_coverage.map(r => (
                <tr key={r.role} className="border-t border-slate-100" data-testid={`mfa-row-${r.role}`}>
                  <td className="px-3 py-2 font-medium">{r.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.totp_enabled)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.sms_enabled)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.dual_mfa)}</td>
                  <td className="px-3 py-2 text-right"><Badge className={TONE_CLS[r.tone]}>{r.any_mfa_pct}%</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Lock className="w-4 h-4 text-violet-500" />Password policy</div>
          <div className="space-y-1 text-[11.5px]">
            <div className="flex items-center justify-between"><span className="text-slate-500">Min length</span><span className="tabular-nums">{data.password_policy.min_length}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Uppercase required</span><span>{data.password_policy.require_uppercase ? "✓" : "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Digit required</span><span>{data.password_policy.require_digit ? "✓" : "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Special char required</span><span>{data.password_policy.require_special ? "✓" : "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Hash algorithm</span><span className="font-mono text-[10.5px]">{data.password_policy.hash_algorithm}</span></div>
            <div className="flex items-center justify-between pt-1"><span className="text-slate-500">FFIEC compliant</span><Badge className={TONE_CLS[data.password_policy.tone]}>{data.password_policy.ffiec_compliant ? "yes" : "gap"}</Badge></div>
          </div>
          <div className="mt-2 text-[10.5px] text-slate-500 italic">{data.password_policy.notes}</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-rose-500" />Lockout attestation</div>
          <div className="space-y-1 text-[11.5px]">
            <div className="flex items-center justify-between"><span className="text-slate-500">Attempts threshold</span><span className="tabular-nums">{data.lockout_attestation.threshold_attempts}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Window (min)</span><span className="tabular-nums">{data.lockout_attestation.window_minutes}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Lockout duration (min)</span><span className="tabular-nums">{data.lockout_attestation.lockout_duration_min}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Failed logins · 24h</span><span className="tabular-nums">{fmt(data.lockout_attestation.failed_logins_24h)}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Lockouts triggered · 24h</span><span className="tabular-nums">{fmt(data.lockout_attestation.lockouts_24h)}</span></div>
            <div className="flex items-center justify-between pt-1"><span className="text-slate-500">FFIEC compliant</span><Badge className={TONE_CLS[data.lockout_attestation.tone]}>{data.lockout_attestation.ffiec_compliant ? "yes" : "gap"}</Badge></div>
          </div>
          <div className="mt-2 text-[10.5px] text-slate-500 italic">{data.lockout_attestation.notes}</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500" />Impossible-travel</div>
          <div className="space-y-1 text-[11.5px]">
            <div className="flex items-center justify-between"><span className="text-slate-500">Detection active</span><Badge className={TONE_CLS.emerald}>yes</Badge></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Alerts (30d)</span><span className="tabular-nums">{fmt(data.impossible_travel.alerts_30d)}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Distinct users logged (30d)</span><span className="tabular-nums">{fmt(data.impossible_travel.users_logged_30d)}</span></div>
          </div>
          <div className="mt-2 text-[10.5px] text-slate-500 italic">{data.impossible_travel.coverage_notes}</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" />Session security</div>
          <div className="space-y-1 text-[11.5px]">
            <div className="flex items-center justify-between"><span className="text-slate-500">JWT expiry (h)</span><span className="tabular-nums">{data.session_security.jwt_expiry_hours}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Refresh-token supported</span><span>{data.session_security.refresh_token_supported ? "✓" : "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Force-logout supported</span><span>{data.session_security.force_logout_supported ? "✓" : "—"}</span></div>
          </div>
          <div className="mt-2 text-[10.5px] text-slate-500 italic">{data.session_security.session_pinning}</div>
        </div>
      </div>

      <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4" data-testid="auth-gaps">
        <div className="font-semibold text-rose-900 flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" />Honest gaps · disclosed
        </div>
        <ul className="space-y-1.5 text-[11.5px] text-rose-900">
          {data.gaps.map((g, i) => (
            <li key={i} className="flex items-start gap-2" data-testid={`auth-gap-${i}`}>
              <Badge className={TONE_CLS[g.severity === "high" ? "rose" : g.severity === "medium" ? "amber" : "slate"]}>{g.severity}</Badge>
              <div>
                <span className="font-medium">{g.gap}</span> — {g.detail}
                <div className="text-[10.5px] text-slate-500 italic mt-0.5">Remediation: {g.remediation}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Wave-38 · REGULATORY REPORTING CALENDAR
// ═════════════════════════════════════════════════════════════════════════
export function BankingRegulatoryCalendar() {
  const [data, setData] = useState(null);
  const [attestOpen, setAttestOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState("");
  const [actorName, setActorName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    api.get("/partner/banking/regulatory-calendar")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load regulatory calendar"));

  useEffect(() => { load(); }, []);

  const submitAttest = async () => {
    if (!target || note.length < 8) {
      toast.error("Note must be ≥8 chars");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/partner/banking/regulatory-calendar/${target.deliverable_id}/attest`, {
        note,
        completed_by_name: actorName || undefined,
      });
      toast.success(`Attested · ${target.title}`);
      setAttestOpen(false);
      setNote("");
      setActorName("");
      setTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attestation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="banking-regulatory-calendar">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarCheck2 className="w-6 h-6 text-amber-500" />
            Regulatory Reporting Calendar
          </h1>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">Wave 38</Badge>
          <Badge variant="outline" className="text-slate-500">Board-level cadence</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total deliverables" value={fmt(data.summary.total)} sub="In calendar" icon={CalendarCheck2} testid="kpi-cal-total" />
        <Kpi label="On track" value={fmt(data.summary.ontrack)} sub=">14d cushion" tone="emerald" icon={CheckCircle2} testid="kpi-cal-ontrack" />
        <Kpi label="Due soon" value={fmt(data.summary.due_soon)} sub="≤14d window" tone="amber" icon={Clock} testid="kpi-cal-soon" />
        <Kpi label="Due now" value={fmt(data.summary.due)} sub="No prior attestation" tone="amber" icon={AlertTriangle} testid="kpi-cal-due" />
        <Kpi label="Overdue" value={fmt(data.summary.overdue)} sub="File immediately" tone="rose" icon={XCircle} testid="kpi-cal-overdue" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-500">
              <th className="px-3 py-2">Deliverable</th>
              <th className="px-3 py-2">Cadence</th>
              <th className="px-3 py-2">Framework</th>
              <th className="px-3 py-2">Last done</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.deliverable_id} className="border-t border-slate-100" data-testid={`calendar-row-${r.deliverable_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">{r.title}</div>
                  <div className="text-[10.5px] text-slate-500 max-w-md">{r.description}</div>
                </td>
                <td className="px-3 py-2">{r.cadence}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-600">{r.framework}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-600">{r.last_done_at ? dateOnly(r.last_done_at) : <span className="italic text-slate-400">never</span>}</td>
                <td className="px-3 py-2">
                  <Badge className={TONE_CLS[r.tone]}>{r.status.replaceAll("_", " ")}</Badge>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {r.days_until < 0
                      ? `Overdue by ${-r.days_until}d`
                      : r.days_until === 0
                        ? "due today"
                        : `${r.days_until}d until next due`}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => { setTarget(r); setAttestOpen(true); }} data-testid={`btn-attest-${r.deliverable_id}`}>
                    <FileCheck2 className="w-3.5 h-3.5 mr-1" />Attest
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700" data-testid="cal-disclaimer">
        <div className="font-semibold text-slate-800 flex items-center gap-2 mb-1.5">
          <BookOpen className="w-4 h-4" /> {data.summary.overall_compliant ? "All on track · no overdue items" : "Some items need attestation"}
        </div>
        {data.disclaimer}
      </div>

      <Dialog open={attestOpen} onOpenChange={setAttestOpen}>
        <DialogContent data-testid="dialog-attest">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-emerald-500" />Attest deliverable complete</DialogTitle>
            <DialogDescription>
              <strong>{target?.title}</strong> · {target?.cadence} · {target?.framework}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11.5px]">Attesting officer (optional)</Label>
              <Input value={actorName} onChange={e => setActorName(e.target.value)} placeholder="e.g. Jane Doe · BSA Officer" data-testid="input-actor-name" />
            </div>
            <div>
              <Label className="text-[11.5px]">Note (≥8 chars · audit-logged)</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Q1-2026 BSA report delivered to board on 2026-03-31 · meeting minutes attached." rows={3} data-testid="input-attest-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttestOpen(false)}>Cancel</Button>
            <Button onClick={submitAttest} disabled={submitting || note.length < 8} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-confirm-attest">
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}
              Attest done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
